import { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { TabBar, TabView } from 'react-native-tab-view';
import styled from 'styled-components/native';

import { useOrientation } from '../../boot/OrientationContext';
import { isWebPlatform } from '../../boot/utils';
import { AnalyticsEvent } from '../../constants/AnalyticsEvent';
import { coral, warrior } from '../../constants/color';
import { white } from '../../constants/color';
import { GET_LAYOUT_MAX_WIDTH } from '../../constants/layout';
import { MktType } from '../../constants/MktType';
import LoadingView from '../../elements/LoadingView';
import { TabBarIndicator, TabBarItem } from '../../elements/TabBar';
import useActions from '../../hooks/useActions';
import useRequest from '../../hooks/useRequest';
import { translate } from '../../i18n';
import { logAnalyticsEvent } from '../../redux/actions/analyticsLog';
import { getFilterConfig } from '../../redux/api';
import {
  Category,
  FilterConfigPage,
  FilterConfigType,
} from '../../types/FilterConfigResponse';
import NewArrivalsPage from './NewArrivalsPage';

interface Route {
  index: number;
  key: string;
  title: string;
  params: Record<string, any>;
}

export const defaultRoute = {
  index: 0,
  key: 'default',
  get title() {
    return translate('new-arrivals-default-tab');
  },
  params: {},
};

export default ({}) => {
  const isLandscape = useOrientation();

  const [tabIndex, setTabIndex] = useState(0);
  const [routes, setRoutes] = useState<Route[]>([]);
  const logEvent = useActions(logAnalyticsEvent);

  const listScrollToTop = useRef({});

  const bindScrollToTop = useCallback((func: Function, key: string) => {
    listScrollToTop.current[key] = func;
  }, []);

  const onTabPress = useCallback(
    ({ route }: { route: Route }) => {
      const { index: routeIndex, key } = route;

      if (tabIndex !== routeIndex) {
        logEvent({
          logEvent: AnalyticsEvent.Click,
          data: {
            pitNumber: routeIndex + 1,
            layoutName: 'new_arrivals_tab',
            mktType: MktType.Word,
            element_content: route.title,
          },
        });
      }

      if (
        tabIndex === routeIndex &&
        typeof listScrollToTop.current[key] === 'function'
      ) {
        listScrollToTop.current[key]();
      }
    },
    [tabIndex, logEvent]
  );

  const renderTabBar = useCallback(
    tabBarProps => {
      return (
        <S.TabBar
          {...tabBarProps}
          scrollEnabled
          tabStyle={styles.tabStyle}
          pressOpacity={1}
          renderIndicator={TabBarIndicator}
          inactiveColor={warrior}
          activeColor={coral}
          onTabPress={onTabPress}
          renderTabBarItem={TabBarItem}
        />
      );
    },
    [onTabPress]
  );

  const renderScene = useCallback(
    ({ route }) => {
      return (
        <NewArrivalsPage
          isVisible={tabIndex === route.index}
          route={route}
          bindScrollToTop={bindScrollToTop}
        />
      );
    },
    [tabIndex]
  );

  const { data } = useRequest(getFilterConfig, {
    params: { page: FilterConfigPage.NewArrivals },
    config: {},
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    const cats = data.items?.find(
      config => config.type === FilterConfigType.Category
    )?.terms;

    let nextRoutes = [defaultRoute];

    if (cats) {
      const catRoutes = cats.map((term, index) => {
        const category = term as Category;

        return {
          index: index + 1,
          key: `${category.id}`,
          title: category.name,
          params: { root_category_ids: [category.id] },
        };
      });

      nextRoutes = [...nextRoutes, ...catRoutes];
    }

    setRoutes(nextRoutes);
  }, [data]);

  useEffect(() => {
    logEvent({
      logEvent: AnalyticsEvent.ViewNewArrivals,
      data: {},
    });
  }, []);

  if (routes.length === 0) {
    return <LoadingView />;
  }

  return (
    <TabView
      lazy
      key={`${isLandscape}`}
      renderTabBar={renderTabBar}
      navigationState={{ index: tabIndex, routes }}
      renderScene={renderScene}
      onIndexChange={setTabIndex}
      initialLayout={initialLayout}
      swipeEnabled={!isWebPlatform}
    />
  );
};

const initialLayout = {
  get width() {
    return GET_LAYOUT_MAX_WIDTH();
  },
};

const S = {
  TabBar: styled(TabBar)`
    background-color: ${white};
  `,
};

const styles = StyleSheet.create({
  tabStyle: {
    width: 'auto',
  },
});
